import React, { useState, useCallback } from 'react';
import { Modal } from '../../';
import { Typography } from 'antd';
import MoveGroupSelector from '../../MoveGroupSelector';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { useBulkAssignMoveGroupToResourcesMutation } from '../../../api';
import './AssignMoveGroupModal.scss';

const { Text } = Typography;

const AssignMoveGroupModal = ({
    open,
    onClose,
    selectedSources = [],
    userOrg,
    onSuccess,
    // Configurable API endpoints
    apiConfig = {
        createMoveGroupEndpoint: () => '/api/v3/movegroups',
        updateResourceEndpoint: (resourceId) => `/api/v3/sources/${resourceId}/move-group`,
        updateResourcePayload: (_resourceId, moveGroupId) => ({ move_group_id: moveGroupId }),
    },
}) => {
    const [moveGroupData, setMoveGroupData] = useState(null);
    const { notifySuccess, notifyError } = useNotificationContext();
    const [bulkAssignMoveGroup, { isLoading }] = useBulkAssignMoveGroupToResourcesMutation();

    const isConfirmDisabled =
        !moveGroupData ||
        (moveGroupData.mode === 'existing' && !moveGroupData.selectedMoveGroupId) ||
        (moveGroupData.mode === 'new' && !moveGroupData.newMoveGroupName?.trim());

    const handleConfirm = useCallback(async () => {
        if (!moveGroupData) {
            notifyError({ description: 'Please select or create a move group.' });
            return;
        }

        // Validate based on mode
        if (moveGroupData.mode === 'existing' && !moveGroupData.selectedMoveGroupId) {
            notifyError({ description: 'Please select a move group.' });
            return;
        }

        if (moveGroupData.mode === 'new' && !moveGroupData.newMoveGroupName?.trim()) {
            notifyError({ description: 'Please enter a move group name.' });
            return;
        }

        try {
            await bulkAssignMoveGroup({
                resources: selectedSources,
                moveGroupData,
                orgId: userOrg,
                createMoveGroupEndpoint: apiConfig.createMoveGroupEndpoint,
                updateResourceEndpoint: apiConfig.updateResourceEndpoint,
                updateResourcePayload: apiConfig.updateResourcePayload,
            }).unwrap();

            notifySuccess({
                description: `Successfully assigned move group to ${selectedSources.length} source${selectedSources.length > 1 ? 's' : ''}.`,
            });
            setMoveGroupData(null);
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error assigning move group:', error);
            notifyError({ description: 'Failed to assign move group. Please try again.' });
        }
    }, [
        moveGroupData,
        selectedSources,
        userOrg,
        notifySuccess,
        notifyError,
        onSuccess,
        onClose,
        bulkAssignMoveGroup,
        apiConfig,
    ]);

    const handleCancel = useCallback(() => {
        setMoveGroupData(null);
        onClose();
    }, [onClose]);

    return (
        <Modal
            title="Add a Move Group to Selected Source(s)"
            open={open}
            onOk={handleConfirm}
            onCancel={handleCancel}
            okText="Confirm"
            cancelText="Cancel"
            okButtonProps={{ disabled: isConfirmDisabled }}
            confirmLoading={isLoading}
            className="assign-move-group-modal"
            width={600}
        >
            <div className="move-group-modal-content">
                <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
                    Assign move group to {selectedSources.length} selected source{selectedSources.length > 1 ? 's' : ''}
                </Text>

                <MoveGroupSelector
                    value={moveGroupData}
                    onChange={setMoveGroupData}
                    userOrg={userOrg}
                    disabled={isLoading}
                />
            </div>
        </Modal>
    );
};

export default AssignMoveGroupModal;
